import React from 'react';
import { useParams } from 'react-router-dom';
import {
  CompanyChart,
  CompanyFileDocumentType,
  FinancialDocumentTaskStates,
} from '@hum/types';
import { ChartList } from '@hum/icm-app/src/components/ChartList';
import {
  Icon,
  ProgressDownload,
  Spinner,
  Splash,
  Text,
  TextType,
} from '@hum/ui-library';
import { PageContent } from '@hum/legacy-ui';
import { INSIGHTS_TABS } from '@hum/icm-app/src/pages/admin/AdminAnalytics/AdminAnalyticsWithCharts';
import { InsightsCard } from './InsightsCard';
import { useApiQuery, getInsightsCharts } from '@hum/api';
import { useDownloadFinancialDocuments } from '../../hooks/useDownloadFinancialDocuments';

export const CompanyFinancialAnalysisView = ({
  companyId,
  withCard,
}: {
  companyId: string;
  withCard?: boolean;
}) => {
  const { chartTabName } = useParams<{ chartTabName?: INSIGHTS_TABS }>();
  const {
    handleDownloadFinancialDocuments,
    isPolling,
    isError,
    task,
  } = useDownloadFinancialDocuments({
    companyId,
    docType: CompanyFileDocumentType.ChartbookInvestorAnalytics,
  });
  const onClickDownloadButton = () => {
    handleDownloadFinancialDocuments();
  };
  const isFailure =
    isError || task?.state === FinancialDocumentTaskStates.Failure;
  const isSuccess = task?.state === FinancialDocumentTaskStates.Success;
  const { data: chartsData } = useApiQuery(getInsightsCharts, {
    params: { companyId },
  });
  const chartsContent = chartsData![chartTabName!];

  if (!chartsData) {
    return <Spinner fullScreen />;
  }

  return (
    <div style={{ height: '100%' }}>
      {
        // @ts-ignore
        chartsContent?.length ? (
          <PageContent fixedWidth padded center>
            {withCard && (
              <InsightsCard
                onDownload={onClickDownloadButton}
                isDownloadingFile={isPolling}
              />
            )}
            <ChartList charts={chartsContent as CompanyChart[]} />
            <ProgressDownload
              isPolling={isPolling}
              isFailure={isFailure}
              isSuccess={isSuccess}
            />
          </PageContent>
        ) : (
          <div className="flex h-[calc(100vh-48px)]">
            <div className="flex flex-col items-center justify-around w-full">
              <Splash
                graphic={
                  <Icon.InvestorSignupStepThree className="max-w-[200px] mx-auto" />
                }
                title="Connect data to see insights"
                subtitle="Connect your accounting software in the Data Sharing tab or upload Income Statement and Balance sheet data in your data room. Some additional insights are also available when you connect to your bank and/or payment processor data as well."
              >
                <Text
                  type={TextType.BODY_S}
                  subdued
                  className="max-w-md mx-auto"
                >
                  Note: Insights can take some time to generate once your data
                  is connected, thank you for your patience
                </Text>
              </Splash>
            </div>
          </div>
        )
      }
    </div>
  );
};
